<script lang="ts">
  import {
    BALANCE,
    emailError,
    passwordStrength,
    usernameError,
    type UsernameError,
  } from '@2k48/shared';
  import TopBar from '../components/TopBar.svelte';
  import PixelAvatar from '../components/PixelAvatar.svelte';
  import PixelButton from '../components/PixelButton.svelte';
  import PixelInput from '../components/PixelInput.svelte';
  import { api } from '../../net/client';
  import { i18n } from '../i18n/index.svelte';
  import { auth } from '../stores/auth.svelte';
  import { router } from '../stores/router.svelte';
  import { session } from '../stores/session.svelte';
  import { toasts } from '../stores/toasts.svelte';
  import { authErrorField, authErrorMessage, emailMessage, usernameMessage } from '../authMessages';

  /**
   * Create an account. Reference 08 · REGISTRO.
   *
   * This screen does not create a player — it claims the one this device has
   * been using. Everything already earned stays put, which is why the request
   * carries the device id and gets the same `playerId` back.
   *
   * The name typed here becomes both the sign-in username and the display name,
   * as the mockup's single field implies. They start identical; afterwards the
   * username is fixed and Perfil renames only the display name.
   */

  const t = $derived(i18n.t);

  const AVATARS = [0, 1, 2, 3];

  let username = $state(session.profile?.displayName ?? '');
  let email = $state('');
  let password = $state('');
  let avatarId = $state(session.profile?.avatarId ?? 0);
  let busy = $state(false);
  let failure = $state<string | null>(null);
  let failedField = $state<string | null>(null);

  /** Only shown once the field has been left alone, so it does not scold mid-word. */
  const touched = $state({ username: false, email: false, password: false });

  const usernameProblem = $derived(usernameError(username));
  const emailProblem = $derived(emailError(email));
  const passwordProblem = $derived(
    password.length === 0
      ? 'required'
      : password.length < BALANCE.account.passwordMinLength
        ? 'too_short'
        : null,
  );
  const strength = $derived(passwordStrength(password));
  const strengthTone = $derived(strength >= 3 ? 'g' : strength === 2 ? 'y' : 'r');

  const valid = $derived(!usernameProblem && !emailProblem && !passwordProblem);
  const canSubmit = $derived(valid && !busy && session.deviceId !== null);

  /**
   * Availability, asked of the server rather than guessed.
   *
   * Debounced because it fires while someone types, and left as a hint rather
   * than a gate: the authoritative answer is the unique index, which decides at
   * insert time. A name that frees up between this check and the submit would
   * otherwise stay refused by a stale "no".
   */
  let available = $state<boolean | null>(null);

  $effect(() => {
    const candidate = username.trim();
    if (usernameError(candidate)) {
      available = null;
      return;
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      void api<{ available: boolean }>(`/auth/username?username=${encodeURIComponent(candidate)}`)
        .then((response) => {
          if (!cancelled) available = response.available;
        })
        .catch(() => {
          // Unknown, not "taken": a failed lookup must not talk a player out of
          // a name that is theirs for the asking.
          if (!cancelled) available = null;
        });
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  });

  function fieldError(problem: UsernameError | null, shown: boolean): string | undefined {
    return shown && problem ? usernameMessage(problem) : undefined;
  }

  async function submit(): Promise<void> {
    const deviceId = session.deviceId;
    if (!canSubmit || !deviceId) return;

    busy = true;
    failure = null;
    failedField = null;

    try {
      const profile = await auth.register({
        deviceId,
        username: username.trim(),
        email: email.trim(),
        password,
        avatarId,
        rememberMe: true,
      });
      session.adopt(profile);
      password = '';
      toasts.success(t.toasts.accountCreated, { key: 'auth:state' });
      router.go('menu');
    } catch (error) {
      failure = authErrorMessage(error);
      failedField = authErrorField(error);
    } finally {
      busy = false;
    }
  }
</script>

<div class="view auth auth--register">
  <TopBar title={t.auth.registerTitle} />

  <form
    class="view__body auth__form"
    onsubmit={(event) => {
      event.preventDefault();
      void submit();
    }}
  >
    <span class="lbl">{t.auth.chooseAvatar}</span>
    <div class="auth__avatars" role="radiogroup" aria-label={t.auth.ariaAvatar}>
      {#each AVATARS as id (id)}
        <button
          type="button"
          class="auth__avatar"
          class:auth__avatar--on={avatarId === id}
          role="radio"
          aria-checked={avatarId === id}
          aria-label={`Avatar ${id + 1}`}
          disabled={busy}
          onclick={() => (avatarId = id)}
        >
          <PixelAvatar avatarId={id} size="fill" state={avatarId === id ? 'active' : 'normal'} />
        </button>
      {/each}
    </div>

    <div class="auth__field">
      <PixelInput
        label={t.auth.playerName}
        name="username"
        bind:value={username}
        autocomplete="username"
        maxlength={BALANCE.account.usernameMaxLength}
        disabled={busy}
        onblur={() => (touched.username = true)}
        error={failedField === 'username'
          ? (failure ?? undefined)
          : fieldError(usernameProblem, touched.username)}
      />
      <!-- One line, always present, so the field below never moves. -->
      <span
        class="auth__hint"
        class:auth__hint--ok={available === true}
        class:auth__hint--bad={available === false}
      >
        {#if available === true}{t.auth.available}{:else if available === false}{t.auth
            .unavailable}{/if}
      </span>
    </div>

    <PixelInput
      label={t.auth.email}
      name="email"
      type="email"
      bind:value={email}
      autocomplete="email"
      maxlength={BALANCE.account.emailMaxLength}
      disabled={busy}
      onblur={() => (touched.email = true)}
      error={failedField === 'email'
        ? (failure ?? undefined)
        : touched.email && emailProblem
          ? emailMessage(emailProblem)
          : undefined}
    />

    <div class="auth__field">
      <PixelInput
        label={t.auth.password}
        name="password"
        type="password"
        bind:value={password}
        autocomplete="new-password"
        maxlength={BALANCE.account.passwordMaxLength}
        disabled={busy}
        onblur={() => (touched.password = true)}
        error={failedField === 'password' ? (failure ?? undefined) : undefined}
      />
      <!--
        A meter, not a gate. The only rule the server enforces is length, and a
        strength bar that refused passwords would be inventing a policy the
        endpoint does not have.
      -->
      <div
        class="prog auth__strength"
        role="meter"
        aria-label={t.auth.ariaStrength}
        aria-valuenow={strength}
        aria-valuemin="0"
        aria-valuemax="4"
      >
        {#each [1, 2, 3, 4] as step (step)}
          <i class={strength >= step ? strengthTone : ''}></i>
        {/each}
      </div>
      <span class="auth__hint" class:auth__hint--bad={touched.password && passwordProblem !== null}>
        {password.length > 0 && password.length < BALANCE.account.passwordMinLength
          ? t.auth.charactersLeft(BALANCE.account.passwordMinLength - password.length)
          : ''}
      </span>
    </div>

    <!-- The tail sits at the bottom of whatever height the device has, so the
         primary action stays within reach instead of stranded mid-screen. -->
    <div class="auth__tail">
      <p class="auth__failure" role="alert">{failedField === null && failure ? failure : ''}</p>

      <PixelButton type="submit" variant="p2" icon="play" disabled={!canSubmit}>
        {busy ? t.auth.creating : t.auth.createAccount}
      </PixelButton>

      <p class="tiny auth__terms">{t.auth.termsLine1}<br />{t.auth.termsLine2}</p>
    </div>
  </form>
</div>
